/**
 * Pending auth-timer bookkeeping
 *
 * `use-auth-error-handler` schedules two module-global timers: the debounced
 * `auth-error` broadcast (`dispatchAuthError`) and the delayed `signOut`
 * redirect (`handleAuthError`). Both outlive whatever scheduled them, and the
 * second performs network I/O — so anything tearing an environment down needs
 * a way to cancel them.
 *
 * The handles live here, in a leaf module with no `next-auth` (or React)
 * import, so the jest global teardown can cancel them without pulling the
 * auth client into every suite's module registry. See VALE-557 — a redirect
 * that survived its own suite fired the real `signOut()` inside a later
 * suite's jsdom, where `fetch` does not exist, and failed an innocent
 * bystander with `ReferenceError: fetch is not defined`.
 */

/**
 * Handle for the pending signOut redirect scheduled by `handleAuthError`, so a
 * later/duplicate call (or a teardown) can cancel it instead of leaving it to
 * fire uncancelled (LF-6403).
 */
let pendingAuthRedirectTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Handle for the pending debounced `auth-error` broadcast scheduled by
 * `dispatchAuthError`. It is what *chains* into the redirect above: when it
 * fires it dispatches the event a mounted `useAuthErrorSync` turns straight
 * into a `handleAuthError` call, so cancelling only the redirect is not enough
 * to guarantee no signOut is left pending (VALE-557).
 */
let authErrorDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Record the pending signOut redirect handle (replacing any previous one). */
export function setPendingAuthRedirectTimer(
  timer: ReturnType<typeof setTimeout> | null
) {
  pendingAuthRedirectTimer = timer;
}

/** Record the pending debounced broadcast handle (replacing any previous one). */
export function setAuthErrorDebounceTimer(
  timer: ReturnType<typeof setTimeout> | null
) {
  authErrorDebounceTimer = timer;
}

/**
 * Cancel a pending signOut redirect scheduled by `handleAuthError`, if any.
 * Called on `useAuthErrorSync` teardown so a redirect triggered just before
 * unmount can't fire after the owning component (and its environment) is
 * gone (LF-6403).
 *
 * Deliberately narrower than {@link cancelPendingAuthTimers}: it leaves a
 * pending `auth-error` broadcast alone, which is the behaviour LF-6403 shipped
 * and the unmount path still wants.
 */
export function cancelPendingAuthRedirect() {
  if (pendingAuthRedirectTimer) {
    clearTimeout(pendingAuthRedirectTimer);
    pendingAuthRedirectTimer = null;
  }
}

/**
 * Cancel a pending debounced `auth-error` broadcast, if any. `dispatchAuthError`
 * calls this to collapse a storm of concurrent 401s into one broadcast.
 */
export function cancelAuthErrorDebounce() {
  if (authErrorDebounceTimer) {
    clearTimeout(authErrorDebounceTimer);
    authErrorDebounceTimer = null;
  }
}

/**
 * Cancel *every* pending auth timer — the debounced broadcast as well as the
 * signOut redirect it can chain into.
 *
 * This is the teardown-grade cancel: after it returns, no auth timer scheduled
 * so far can fire. The jest global teardown calls it after each test so a
 * timer can never cross a suite boundary (VALE-557).
 */
export function cancelPendingAuthTimers() {
  cancelPendingAuthRedirect();
  cancelAuthErrorDebounce();
}
